import React, { useEffect, useLayoutEffect, useState } from 'react'
import {
  Button,
  ButtonGroup,
  Flex,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Link,
  NumberInput,
  NumberInputField,
  Text,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react'
import { userState, gameState, modalState } from './store'
import { SiRipple } from 'react-icons/si'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { getMsPassed, growth, numberWithCommas } from './utils'
import useLocalStorage from './useLocalStorage'
import socket from './socket'

export default function BetControls() {
  const [user, setUser] = useRecoilState(userState)
  const setModal = useSetRecoilState(modalState)
  const toast = useToast()
  const [isLargerThan1280] = useMediaQuery('(min-width: 992px)')
  const [isSmallerThan576] = useMediaQuery('(max-width: 575px)')

  const [game, setGame] = useRecoilState(gameState)

  const [isPreBet, setIsPreBet] = useState(false)

  const [betAmount, setBetAmount] = useLocalStorage('betAmount', 0)
  const [autoCashOut, setAutoCashOut] = useLocalStorage('autoCashOut', 2)

  const [multiplier, setMultiplier] = useState(100)

  const [isLoading, setIsLoading] = useState(false)

  useLayoutEffect(() => {
    let animationFrameId

    function render() {
      const msPassed = getMsPassed(game.startDate, game.endDate)
      setMultiplier(growth(msPassed))
      animationFrameId = requestAnimationFrame(render)
    }
    if (game.state === 'STARTED' && user.state === 'BETTING') render()

    return () => cancelAnimationFrame(animationFrameId)
  }, [game, user])

  useEffect(() => {
    socket.on('user-bet', data => {
      setIsLoading(false)

      if (data.error)
        return toast({
          title: data.error,
          status: 'error',
          duration: 9000,
          isClosable: true,
        })

      const { amount, username, id } = data

      setGame(prevState => {
        const bets = [...prevState.bets, { amount, username, id }].sort(
          (a, b) => b.amount - a.amount
        )

        return { ...prevState, bets }
      })

      setUser(prevState => {
        if (!prevState.isLoggedIn || username !== prevState.username)
          return { ...prevState }

        return {
          ...prevState,
          currentBet: amount,
          state: 'BETTING',
          balance: prevState.balance - amount,
        }
      })

      // onClose()

      // setUser(prevState => ({
      //   ...prevState,
      //   username: data.username,
      //   balance: data.balance,
      //   isLoggedIn: true,
      // }))

      // localStorage.setItem('token', data.token)
    })

    socket.on('user-cashout', async data => {
      setIsLoading(false)

      if (data.error)
        return toast({
          title: data.error,
          status: 'error',
          duration: 9000,
          isClosable: true,
        })

      const { multiplier, username } = data

      setUser(prevState => {
        if (username !== prevState.username) return prevState

        return {
          ...prevState,
          balance:
            prevState.balance +
            Math.floor(prevState.currentBet * (multiplier / 100)),
          currentBet: null,
          state: 'IDLE',
        }
      })

      setGame(prevState => {
        const updatedBets = prevState.bets.map(x =>
          x.username === username ? { ...x, multiplier } : x
        )
        return { ...prevState, bets: updatedBets }
      })
    })

    return () => {
      socket.off('user-bet')
      socket.off('user-cashout')
    }
  }, [])

  useEffect(() => {
    if (game.state === 'WAITING' && isPreBet) {
      socket.emit('user-bet', {
        betAmount: betAmount * 100,
        autoCashOut: autoCashOut * 100,
      })
      setIsLoading(true)
      setIsPreBet(false)
    }
  }, [game, betAmount, isPreBet, autoCashOut])

  let buttonText
  if (user.state === 'BETTING') {
    buttonText =
      game.state === 'STARTED'
        ? `Cash out\n
        @ ${numberWithCommas(user.currentBet * (multiplier / 100))}`
        : 'Betting'
  } else if (isPreBet) {
    buttonText = 'Betting (cancel)'
  } else {
    buttonText = 'Place bet'
  }

  let isButtonDisabled
  if (
    (user.state === 'BETTING' && game.state === 'WAITING') ||
    game.state === 'LOADING' ||
    isLoading
  ) {
    isButtonDisabled = true
  } else if (
    ((betAmount < game.minBet / 100 ||
      betAmount > user.balance / 100 ||
      betAmount > game.maxBet / 100) &&
      !user.currentBet) ||
    !autoCashOut ||
    autoCashOut <= 1
  ) {
    isButtonDisabled = true
  } else {
    isButtonDisabled = false
  }

  function buttonAction(e) {
    e.preventDefault()
    if (user.currentBet) {
      setIsLoading(true)
      socket.emit('user-cashout')
    } else {
      if (game.state === 'WAITING') {
        setIsLoading(true)
        socket.emit('user-bet', {
          betAmount: betAmount * 100,
          autoCashOut: autoCashOut * 100,
        })
      } else {
        setIsPreBet(!isPreBet)
      }
    }
  }

  if (!user?.isLoggedIn)
    return (
      <Text fontSize="xl">
        <Link onClick={() => setModal('login')} color="blue.500">
          Login
        </Link>{' '}
        or{' '}
        <Link onClick={() => setModal('register')} color="blue.500">
          Register
        </Link>{' '}
        to start playing.
      </Text>
    )

  return (
    <form style={{ height: '100%' }} onSubmit={buttonAction}>
      <Flex h="100%" flexDir={'column'}>
        {isLargerThan1280 && (
          <ButtonGroup
            isDisabled={isPreBet || user.state === 'BETTING'}
            colorScheme="blue"
            mb={'3'}
            spacing="0"
            size={isLargerThan1280 ? 'md' : 'xs'}
          >
            <Button
              onClick={() => {
                setBetAmount(
                  betAmount => Math.floor((betAmount / 2) * 100) / 100
                )
              }}
              borderRightRadius="0"
            >
              1/2
            </Button>
            <Button
              onClick={() => {
                setBetAmount(betAmount => Math.floor(betAmount * 2 * 100) / 100)
              }}
              colorScheme="blue"
              borderRadius="0"
            >
              x2
            </Button>
            <Button
              onClick={() => {
                setBetAmount(game.minBet / 100)
              }}
              colorScheme="blue"
              borderRadius="0"
            >
              min
            </Button>
            <Button
              onClick={() => {
                setBetAmount(
                  user?.balance < game.maxBet
                    ? user?.balance / 100
                    : game.maxBet / 100 || 0
                )
              }}
              colorScheme="blue"
              borderLeftRadius="0"
            >
              max
            </Button>
          </ButtonGroup>
        )}

        <NumberInput
          defaultValue={betAmount}
          value={betAmount}
          isDisabled={isPreBet || user.state === 'BETTING'}
          onBlur={e => setBetAmount(Math.floor(e.target.value * 100) / 100)}
        >
          <InputGroup size={isSmallerThan576 ? 'sm' : 'md'}>
            <InputLeftAddon children="Bet" />
            <NumberInputField
              onChange={e => setBetAmount(e.target.value)}
              borderRadius="0"
            />
            <InputRightAddon children={<SiRipple />} />
          </InputGroup>
        </NumberInput>

        <NumberInput
          defaultValue={autoCashOut}
          value={autoCashOut}
          isDisabled={isPreBet || user.state === 'BETTING'}
          my={isSmallerThan576 ? '1' : '3'}
          onBlur={e => setAutoCashOut(Math.floor(e.target.value * 100) / 100)}
        >
          <InputGroup size={isSmallerThan576 ? 'sm' : 'md'}>
            <InputLeftAddon children="Cash Out" />
            <NumberInputField
              onChange={e => setAutoCashOut(e.target.value)}
              borderRadius="0"
            />
            <InputRightAddon children="x" />
          </InputGroup>
        </NumberInput>
        <Button
          flex="1"
          colorScheme="orange"
          fontSize={isSmallerThan576 ? 'xl' : '3xl'}
          w="100%"
          type="submit"
          disabled={isButtonDisabled}
          isLoading={isLoading}
        >
          {buttonText}
        </Button>
      </Flex>
    </form>
  )
}
